import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import type { LandingPremiatedNumberItem } from '@/types/landing'
import { LandingPremiatedNumbersList } from './LandingPremiatedNumbersList'
import styles from './LandingPremiatedNumbersExpandable.module.css'

type LandingPremiatedNumbersExpandableProps = {
  items: LandingPremiatedNumberItem[]
  donationOnly: boolean
  visualVariant: 'light' | 'dark'
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
}

export function LandingPremiatedNumbersExpandable({
  items,
  donationOnly,
  visualVariant,
  expanded,
  onExpandedChange,
}: LandingPremiatedNumbersExpandableProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [panelHeight, setPanelHeight] = useState(0)

  const updatePanelHeight = useCallback(() => {
    const node = contentRef.current
    if (!node) return
    setPanelHeight(node.scrollHeight)
  }, [])

  useLayoutEffect(() => {
    updatePanelHeight()
  }, [items, expanded, updatePanelHeight])

  useEffect(() => {
    const node = contentRef.current
    if (!node || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      updatePanelHeight()
    })
    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [updatePanelHeight])

  useEffect(() => {
    if (!expanded) return

    const handleResize = () => {
      updatePanelHeight()
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [expanded, updatePanelHeight])

  if (items.length === 0) {
    return null
  }

  const panelId = useId()
  const headingId = useId()
  const variantClass = visualVariant === 'dark' ? styles.variantDark : styles.variantLight

  return (
    <section
      className={`${styles.shell} ${variantClass} ${expanded ? styles.shellExpanded : ''}`}
      aria-labelledby={headingId}
    >
      <div className={styles.summary}>
        <div className={styles.summaryMain}>
          <Icon icon="mdi:ticket-percent-outline" aria-hidden className={styles.summaryIcon} />
          <div>
            <strong id={headingId} className={styles.summaryTitle}>
              Títulos premiados
            </strong>
            <span className={styles.summarySubtitle}>
              {donationOnly
                ? 'Prêmios e ganhadores já divulgados — confira agora.'
                : 'Números premiados e ganhadores — confira agora.'}
            </span>
          </div>
        </div>
        <button
          type="button"
          className={`${styles.toggleButton} ${expanded ? styles.toggleButtonExpanded : ''}`}
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => onExpandedChange(!expanded)}
        >
          <Icon
            icon="mdi:chevron-down"
            aria-hidden
            className={`${styles.toggleIcon} ${expanded ? styles.toggleIconExpanded : ''}`}
          />
          <span className={styles.toggleLabel}>{expanded ? 'Ocultar lista' : 'Abrir lista'}</span>
        </button>
      </div>

      <div
        id={panelId}
        className={`${styles.expandPanel} ${expanded ? styles.expandPanelOpen : ''}`}
        style={{ maxHeight: expanded ? panelHeight : 0 }}
        aria-hidden={!expanded}
        inert={!expanded}
      >
        <div ref={contentRef} className={styles.expandPanelContent}>
          <p className={styles.expandEyebrow}>Sorteios realizados</p>
          <LandingPremiatedNumbersList items={items} visualVariant={visualVariant} />
        </div>
      </div>
    </section>
  )
}
